import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
	motion,
	useReducedMotion,
	useScroll,
	useTransform,
} from "framer-motion";
import { ShoppingBag, ShoppingCart, Mail } from "lucide-react";
import { supabase } from "../lib/supabase";
import { Product, PRODUCT_SELECT } from "../types/product";
import { useCartStore } from "../store/cartStore";
import { useNotification } from "../hooks/useNotification";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Reveal } from "../components/Reveal";
import SocialButtons from "../components/SocialButtons";
import heroImage from "../assets/images/hero-arnie-montagna.webp";
import storiaImage from "../assets/images/arnie_campo.jpg";
import { productImage } from "../lib/productImage";
import { isSoldOut, onlyVariant, priceLabel } from "../lib/variants";
import { featureIcon } from "../lib/siteContent";
import { useSiteContent } from "../hooks/useSiteContent";
import { usePosts } from "../hooks/usePosts";
import { groupPosts } from "../types/post";
import PostCard from "../components/PostCard";

export default function Home() {
	const [products, setProducts] = useState<Product[]>([]);
	const [loading, setLoading] = useState(true);
	const addItem = useCartStore((state) => state.addItem);
	const navigate = useNavigate();
	const { success: notifySuccess } = useNotification();
	const { content: hero, loaded: contentLoaded } = useSiteContent("home.hero");
	const { content: stats } = useSiteContent("home.stats");
	const { content: story } = useSiteContent("home.story");
	const { content: features } = useSiteContent("home.features");
	const { content: cta } = useSiteContent("home.cta");
	const { content: contacts } = useSiteContent("site.contacts");
	const hasSocial = Object.values(contacts.social).some(Boolean);
	const { posts } = usePosts();
	// Prima i prossimi eventi, poi le ultime novità: al massimo tre schede.
	const { upcoming, news } = groupPosts(posts);
	const homePosts = [...upcoming, ...news].slice(0, 3);

	useEffect(() => {
		async function fetchProducts() {
			setLoading(true);
			const { data, error } = await supabase
				.from("products")
				.select(PRODUCT_SELECT)
				.eq("active", true)
				.order("created_at", { ascending: false })
				.limit(6);

			if (error) {
				console.error("Error fetching products:", error);
			} else {
				setProducts(data || []);
			}
			setLoading(false);
		}

		fetchProducts();
	}, []);

	// Con un solo formato si aggiunge subito; con più formati si sceglie
	// nella pagina del prodotto.
	const handleAddToCart = (e: React.MouseEvent, product: Product) => {
		e.preventDefault();
		e.stopPropagation();

		const single = onlyVariant(product);
		if (!single) {
			navigate(`/products/${product.id}`);
			return;
		}
		addItem(product, single);
		notifySuccess(`${product.name} aggiunto al carrello`);
	};

	const featuredProduct = products[0];

	const scrollToStory = () => {
		document.getElementById("storia")?.scrollIntoView({ behavior: "smooth" });
	};

	const reducedMotion = useReducedMotion();

	const heroRef = useRef<HTMLElement>(null);
	const { scrollYProgress: heroProgress } = useScroll({
		target: heroRef,
		offset: ["start start", "end start"],
	});
	const heroImgScale = useTransform(heroProgress, [0, 1], [1, 1.12]);
	const heroImgY = useTransform(heroProgress, [0, 1], ["0%", "10%"]);

	const storiaRef = useRef<HTMLElement>(null);
	const { scrollYProgress: storiaProgress } = useScroll({
		target: storiaRef,
		offset: ["start end", "end start"],
	});
	const storiaImgScale = useTransform(storiaProgress, [0, 1], [1.05, 1.18]);

	return (
		<div>
			{/* Hero */}
			<section ref={heroRef} className="relative overflow-hidden bg-brand">
				{/* Fino alla risposta del DB niente foto né testi: altrimenti si
				    vedrebbero per un attimo quelli predefiniti al posto dei salvati. */}
				{contentLoaded && (
					<motion.img
						src={hero.image ?? heroImage}
						alt=""
						className="absolute inset-0 h-full w-full object-cover"
						style={
							reducedMotion ? undefined : { scale: heroImgScale, y: heroImgY }
						}
					/>
				)}
				<div className="absolute inset-0 bg-gradient-to-r from-brand/95 via-brand/85 to-brand/60" />
				<div className="container relative z-10 py-20 md:py-28">
					<div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-12 items-center">
						{/* Invisibile ma presente finché il DB non risponde, così l'altezza
						    non salta; la key fa ripartire le animazioni con i testi veri. */}
						<div
							key={String(contentLoaded)}
							className={contentLoaded ? undefined : "invisible"}
						>
							{hero.eyebrow && (
								<Reveal index={0}>
									<p className="ap-eyebrow text-sm font-bold uppercase tracking-widest text-brand-accent mb-4">
										{hero.eyebrow}
									</p>
								</Reveal>
							)}
							<Reveal index={1}>
								<h1 className="font-display text-4xl md:text-6xl font-bold text-white leading-tight mb-6">
									{hero.title}
								</h1>
							</Reveal>
							<Reveal index={2}>
								<p className="text-lg text-white/90 max-w-xl mb-10 leading-relaxed whitespace-pre-line">
									{hero.text}
								</p>
							</Reveal>

							<Reveal index={3} className="flex flex-wrap gap-4 mb-12">
								<Button asChild size="lg" shape="pill" variant="accent">
									<Link to="/products">
										<ShoppingBag className="h-5 w-5" />
										{hero.ctaLabel}
									</Link>
								</Button>
								<Button
									size="lg"
									shape="pill"
									variant="outline"
									className="border-white/40 bg-white/10 text-white hover:bg-white/20 hover:text-white"
									onClick={scrollToStory}
								>
									La Nostra Storia
								</Button>
							</Reveal>

							<Reveal
								index={4}
								y={18}
								className="grid grid-cols-3 gap-6 max-w-md"
							>
								{stats.items.map((stat, i) => (
									<div key={i}>
										<p className="font-display text-2xl font-bold text-brand-accent">
											{stat.value}
										</p>
										<p className="text-xs text-white/70 mt-1">{stat.label}</p>
									</div>
								))}
							</Reveal>
						</div>

						{featuredProduct && (
							<Reveal index={2} y={36} className="hidden lg:block">
								<Link
									to={`/products/${featuredProduct.id}`}
									className="block bg-card rounded-2xl shadow-2xl p-4 hover:-translate-y-1 transition-transform"
								>
									<div className="relative rounded-xl overflow-hidden mb-4">
										<img
											src={productImage(featuredProduct.image_url)}
											alt={featuredProduct.name}
											className="w-full h-56 object-cover"
										/>
										<Badge variant="peach" className="absolute top-3 left-3">
											Edizione Limitata
										</Badge>
									</div>
									<p className="ap-eyebrow text-xs font-bold uppercase tracking-widest text-muted-foreground mb-1">
										In evidenza
									</p>
									<h3 className="font-display text-lg font-semibold text-foreground mb-2">
										{featuredProduct.name}
									</h3>
									<p className="text-sm text-muted-foreground line-clamp-2 mb-3">
										{featuredProduct.description}
									</p>
									<p className="ap-price text-lg font-bold text-primary">
										{priceLabel(featuredProduct)}
									</p>
								</Link>
							</Reveal>
						)}
					</div>
				</div>
			</section>

			{/* La Nostra Storia */}
			<section
				ref={storiaRef}
				id="storia"
				className="relative overflow-hidden bg-[#2F1500] min-h-[86svh] flex items-end"
			>
				{contentLoaded && (
					<motion.img
						src={story.image ?? storiaImage}
						alt=""
						className="absolute inset-0 h-full w-full object-cover"
						style={reducedMotion ? undefined : { scale: storiaImgScale }}
					/>
				)}
				<div className="absolute inset-0 bg-gradient-to-t from-[#2F1500]/90 via-[#2F1500]/55 to-[#2F1500]/25" />
				<div className="container relative z-10 py-24 md:py-32">
					<Reveal index={0} className="max-w-3xl">
						<span className="block w-12 h-1 rounded-full bg-[#FFB95F] mb-6" />
						<h2 className="font-display text-3xl md:text-5xl font-bold text-white leading-tight text-balance">
							{story.title}
						</h2>
						<p className="mt-6 max-w-2xl text-white/90 text-base md:text-lg leading-relaxed whitespace-pre-line">
							{story.text}
						</p>
					</Reveal>
				</div>
			</section>

			{/* Punti di forza */}
			<section className="bg-muted/60 py-20">
				<div className="container">
					<div className="text-center max-w-2xl mx-auto mb-16">
						<h2 className="font-display text-3xl font-semibold text-foreground mb-4">
							{features.title}
						</h2>
						<p className="text-muted-foreground">{features.intro}</p>
					</div>

					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
						{features.items.map((feature, i) => {
							const Icon = featureIcon(feature.icon);
							return (
								<Reveal
									key={i}
									index={i}
									className="bg-card rounded-2xl border border-border shadow-sm p-6"
								>
									<div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-5">
										<Icon className="h-6 w-6 text-primary" />
									</div>
									<h3 className="font-display text-lg font-semibold text-foreground mb-2">
										{feature.title}
									</h3>
									<p className="text-sm text-muted-foreground leading-relaxed">
										{feature.description}
									</p>
								</Reveal>
							);
						})}
					</div>
				</div>
			</section>

			{/* Prodotti in evidenza */}
			<section className="py-20">
				<div className="container">
					<div className="mb-12">
						<h2 className="font-display text-3xl font-semibold text-foreground mb-3">
							I Nostri Mieli Pregiati
						</h2>
						<p className="text-muted-foreground max-w-2xl">
							Estratti a freddo tramite decantazione naturale, senza alcuna
							filtrazione industriale aggressiva.
						</p>
					</div>

					{loading ? (
						<div className="flex justify-center py-16">
							<div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary" />
						</div>
					) : (
						<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
							{products.map((product, i) => (
								<Reveal key={product.id} index={i} className="h-full">
									<Link
										to={`/products/${product.id}`}
										className="group flex flex-col h-full bg-card rounded-2xl border border-border shadow-sm overflow-hidden hover:shadow-lg transition-shadow"
									>
										<div className="relative h-52 overflow-hidden">
											<img
												src={productImage(product.image_url)}
												alt={product.name}
												loading="lazy"
												className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
											/>
											{product.category && (
												<Badge
													variant="secondary"
													className="absolute top-3 left-3"
												>
													{product.category}
												</Badge>
											)}
										</div>
										<div className="flex flex-col flex-1 p-5">
											<h3 className="font-display text-lg font-semibold text-foreground mb-2">
												{product.name}
											</h3>
											<p className="text-sm text-muted-foreground flex-1 mb-4 line-clamp-2">
												{product.description}
											</p>
											<div className="flex items-center justify-between mb-4">
												<span className="ap-price text-lg font-bold text-primary">
													{priceLabel(product)}
												</span>
												{isSoldOut(product) && (
													<Badge variant="destructive">Esaurito</Badge>
												)}
											</div>
											<Button
												onClick={(e) => handleAddToCart(e, product)}
												disabled={isSoldOut(product)}
												className="w-full"
											>
												<ShoppingCart className="h-4 w-4" />
												{onlyVariant(product)
													? "Aggiungi al carrello"
													: "Scegli il formato"}
											</Button>
										</div>
									</Link>
								</Reveal>
							))}
						</div>
					)}

					<div className="text-center mt-12">
						<Button asChild variant="outline" size="lg" shape="pill">
							<Link to="/products">Catalogo Completo</Link>
						</Button>
					</div>
				</div>
			</section>

			{/* Eventi e novità: solo se c'è qualcosa da mostrare */}
			{homePosts.length > 0 && (
				<section className="pb-20">
					<div className="container">
						<div className="mb-12">
							<h2 className="font-display text-3xl font-semibold text-foreground mb-3">
								{upcoming.length > 0 ? "Prossimi Eventi e Novità" : "Le Ultime Novità"}
							</h2>
							<p className="text-muted-foreground max-w-2xl">
								Dove trovarci e cosa succede in apiario.
							</p>
						</div>
						<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
							{homePosts.map((post, i) => (
								<Reveal key={post.id} index={i} className="h-full">
									<PostCard post={post} />
								</Reveal>
							))}
						</div>
						<div className="text-center mt-12">
							<Button asChild variant="outline" size="lg" shape="pill">
								<Link to="/novita">Tutti gli Eventi e le Novità</Link>
							</Button>
						</div>
					</div>
				</section>
			)}

			{/* Social: solo se c'è almeno un profilo compilato */}
			{hasSocial && (
				<section className="bg-muted/60 py-16">
					<div className="container max-w-xl text-center">
						<h2 className="font-display text-3xl font-semibold text-foreground mb-3">
							Seguici
						</h2>
						<p className="text-muted-foreground mb-8">
							Foto dagli apiari, il lavoro di ogni stagione e le novità del
							raccolto.
						</p>
						<SocialButtons />
					</div>
				</section>
			)}

			{/* Callout finale */}
			<section className="bg-brand">
				<div className="container py-16 text-center">
					<h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-4">
						{cta.title}
					</h2>
					<p className="text-white/85 max-w-2xl mx-auto mb-8 leading-relaxed whitespace-pre-line">
						{cta.text}
					</p>
					<div className="flex flex-wrap justify-center gap-4">
						<Button
							asChild
							size="lg"
							shape="card"
							className="bg-white text-brand hover:bg-white/90"
						>
							<Link to="/contact">
								<Mail className="h-4 w-4" />
								Contattaci
							</Link>
						</Button>
						<Button
							asChild
							size="lg"
							shape="card"
							variant="outline"
							className="border-white/50 text-white hover:bg-white/10 hover:text-white bg-transparent"
						>
							<Link to="/cart">
								<ShoppingCart className="h-4 w-4 " />
								Vai al Carrello
							</Link>
						</Button>
					</div>
				</div>
			</section>
		</div>
	);
}
